import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, FolderKanban, Loader2, Mic, Square, Upload, X } from 'lucide-react';
import { useSecretaryStore } from './secretaryStore';
import { secretaryApi, type SecretaryProject } from './secretaryApi';
import config from '@/lib/config';

const PANEL = {
  background: 'rgba(10, 13, 18, 0.88)',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  fontFamily: "'Exo 2', sans-serif",
} as const;

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * Минимальный инструмент записи ГиперСекретаря (PRD §12.1). Окна/модуля секретаря в этом
 * приложении нет — мы только записываем разговор и отправляем на бэкенд секретаря, который
 * СНАЧАЛА сохраняет аудио в S3, потом обрабатывает. Здесь — запись → стоп → «отправлено».
 */
export function SecretaryRecorderSheet() {
  const store = useSecretaryStore();
  const { isSheetOpen, recordingStatus, sessionId, durationSec } = store;

  const [error, setError] = useState<string | null>(null);
  const [kbInset, setKbInset] = useState(0);
  const [waveform, setWaveform] = useState<number[]>(new Array(28).fill(0));

  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  // Проект записи. Без него протокол в папку и итоги в канал висят «на
  // согласовании», пока проект не выберут на странице Секретаря (07.10.2026).
  const [projects, setProjects] = useState<SecretaryProject[] | null>(null);
  const [projectId, setProjectId] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Досылка записи во время встречи (19.10.2026): Секретарь разбирает уже
  // пришедшее, и протокол готов через минуту-две после «Стоп», а не через
  // полчаса. Заодно записанное не пропадёт, если телефон сядет посреди встречи.
  const sentRef = useRef(0); // сколько кусков MediaRecorder уже ушло
  const seqRef = useRef(0); // номер следующей посылки
  const sendingRef = useRef<Promise<void> | null>(null);
  const sendTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // iOS keyboard lift (harmless on desktop).
  useEffect(() => {
    const vv = (window as unknown as { visualViewport?: VisualViewport }).visualViewport;
    if (!vv) return;
    const update = () => setKbInset(Math.max(0, window.innerHeight - (vv.height + vv.offsetTop)));
    update();
    vv.addEventListener('resize', update);
    return () => vv.removeEventListener('resize', update);
  }, []);

  // ESC closes when not recording.
  useEffect(() => {
    if (!isSheetOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && recordingStatus !== 'recording') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSheetOpen, recordingStatus]);

  useEffect(() => () => cleanupAudio(), []);

  useEffect(() => {
    if (!isSheetOpen || projects !== null) return;
    let cancelled = false;
    secretaryApi
      .listProjects()
      .then((list) => {
        if (cancelled) return;
        setProjects(list);
        const last = secretaryApi.rememberedProject();
        if (last && list.some((p) => p.id === last)) setProjectId(last);
        else if (list.length === 1) setProjectId(list[0].id);
      })
      .catch(() => !cancelled && setProjects([]));
    return () => {
      cancelled = true;
    };
  }, [isSheetOpen, projects]);

  function chooseProject(id: string) {
    setProjectId(id);
    if (id) secretaryApi.rememberProject(id);
  }

  if (!isSheetOpen) return null;

  function chunkName(type: string): string {
    return type.startsWith('audio/mp4') || type.startsWith('audio/aac') ? 'meeting.m4a' : 'meeting.webm';
  }

  /** Отправить накопившееся с прошлой посылки. Сбой не страшен: эти куски
   *  уйдут следующей посылкой или при «Стоп». */
  function sendPending(sid: string): Promise<void> {
    const run = async () => {
      const upto = chunksRef.current.length;
      if (upto <= sentRef.current) return;
      const pieces = chunksRef.current.slice(sentRef.current, upto);
      const type = chunksRef.current[0]?.type || 'audio/webm';
      await secretaryApi.uploadChunk(sid, seqRef.current, 0, new Blob(pieces, { type }), chunkName(type));
      sentRef.current = upto;
      seqRef.current += 1;
    };
    const prev = sendingRef.current ?? Promise.resolve();
    const next = prev.catch(() => undefined).then(run);
    sendingRef.current = next;
    return next;
  }

  function cleanupAudio() {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    if (sendTimerRef.current) clearInterval(sendTimerRef.current);
    try {
      audioCtxRef.current?.close();
    } catch {
      /* ignore */
    }
    audioCtxRef.current = null;
    analyserRef.current = null;
  }

  function handleClose() {
    if (recordingStatus === 'recording') return;
    cleanupAudio();
    store.reset();
    store.closeSheet();
    setError(null);
    setWaveform(new Array(28).fill(0));
  }

  function pickMime(): string | undefined {
    return [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4;codecs=mp4a.40.2',
      'audio/mp4',
      'audio/aac',
    ].find((t) => MediaRecorder.isTypeSupported(t));
  }

  async function handleStart() {
    setError(null);
    try {
      const session = await secretaryApi.createSession({
        project_id: projectId || null,
        meeting_type: 'other',
        source: 'assistentus_app',
        // Языки не навязываем: Секретарь берёт их из настроек организации
        // (у узбекских компаний — ru,uz). Раньше здесь стояло ru,en.
        language_hints: [],
        participants: [],
        consent_state: 'captured',
        metadata: {},
      });
      store.setSessionId(session.id);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      sentRef.current = 0;
      seqRef.current = 0;
      sendingRef.current = null;

      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 64;
      src.connect(analyser);
      analyserRef.current = analyser;
      const tick = () => {
        if (!analyserRef.current) return;
        const data = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(data);
        setWaveform(Array.from(data).slice(0, 28).map((v) => v / 255));
        animRef.current = requestAnimationFrame(tick);
      };
      tick();

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => stream.getTracks().forEach((t) => t.stop());
      mr.start(5000);
      sendTimerRef.current = setInterval(() => {
        sendPending(session.id).catch(() => undefined);
      }, 60000);

      store.setRecordingStatus('recording');
      store.setDurationSec(0);
      timerRef.current = setInterval(
        () => store.setDurationSec(useSecretaryStore.getState().durationSec + 1),
        1000,
      );
    } catch (e) {
      setError(`Не удалось начать запись: ${String(e).slice(0, 120)}`);
      store.setRecordingStatus('failed');
    }
  }

  async function handleStop() {
    const mr = mediaRecorderRef.current;
    const sid = sessionId;
    if (!mr || !sid) return;
    cleanupAudio();
    setWaveform(new Array(28).fill(0));

    const stopped = new Promise<void>((resolve) => {
      mr.addEventListener('stop', () => resolve(), { once: true });
    });
    if (mr.state !== 'inactive') mr.stop();
    await stopped;

    store.setRecordingStatus('uploading');
    try {
      // 1) Досылаем остаток после последней посылки → бэкенд СОХРАНЯЕТ его
      //    в S3 (до обработки). Посылки во время встречи уже там.
      await sendPending(sid);
      // 2) Stop → бэкенд берёт аудио из S3 и запускает обработку в фоне.
      await secretaryApi.stopSession(sid);
      store.setRecordingStatus('done');
    } catch (e) {
      setError(`Не удалось отправить запись: ${String(e).slice(0, 120)}`);
      store.setRecordingStatus('failed');
    }
  }

  /**
   * Отдать секретарю готовую запись из телефона.
   *
   * Формат не проверяем здесь: за нас это делает сервер, а он умеет всё, что
   * умеет ffmpeg — записи диктофонов Android (m4a, 3gp, amr) и iOS (m4a, caf),
   * пересланные голосовые, mp3, wav, ogg.
   */
  async function handleFile(file: File | null) {
    if (!file) return;
    setError(null);
    setUploadPercent(0);
    store.setRecordingStatus('uploading');
    try {
      const session = await secretaryApi.uploadRecording(
        file,
        { title: file.name.replace(/\.[^.]+$/, ''), project_id: projectId || undefined },
        (p) => setUploadPercent(p),
      );
      store.setSessionId(session.id);
      store.setRecordingStatus('done');
    } catch (e) {
      setError(`Не удалось отправить запись: ${String(e).slice(0, 160)}`);
      store.setRecordingStatus('failed');
    } finally {
      setUploadPercent(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  const recording = recordingStatus === 'recording';
  const uploading = recordingStatus === 'uploading';
  const done = recordingStatus === 'done';
  const secretaryUrl = `https://secretary.${config.MAIN_DOMAIN}`;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={recordingStatus === 'idle' || done ? handleClose : undefined}
    >
      <div
        className="w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
        style={{ ...PANEL, marginBottom: kbInset, paddingBottom: 'env(safe-area-inset-bottom)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/8">
          <div className="flex items-center gap-2 text-white">
            <Mic className="w-4 h-4 text-[#0abab5]" />
            <span className="text-base font-semibold">Секретарь</span>
          </div>
          <button
            onClick={handleClose}
            disabled={recording || uploading}
            className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5 disabled:opacity-30"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 flex flex-col items-center gap-5">
          {error && (
            <div className="w-full rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-3 py-2">
              {error}
            </div>
          )}

          {recordingStatus === 'idle' && (
            <>
              <p className="text-sm text-white/60 text-center">
                Запись разговора для протокола. Аудио сохраняется и обрабатывается на стороне секретаря.
              </p>
              <label className="w-full flex flex-col gap-1.5">
                <span className="flex items-center gap-1.5 text-[12px] text-white/50">
                  <FolderKanban className="w-3.5 h-3.5" /> Проект
                </span>
                {projects === null ? (
                  <span className="text-[13px] text-white/40 py-2">Загружаю проекты…</span>
                ) : (
                  <select
                    value={projectId}
                    onChange={(e) => chooseProject(e.target.value)}
                    className="w-full rounded-lg bg-white/[0.04] border border-white/15 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-[#0abab5] cursor-pointer"
                  >
                    <option value="" className="bg-[#0a0d12]">
                      Без проекта — выберу потом
                    </option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id} className="bg-[#0a0d12]">
                        {p.name}
                      </option>
                    ))}
                  </select>
                )}
              </label>
              <button
                onClick={handleStart}
                className="w-20 h-20 rounded-full bg-[#0abab5]/20 border-2 border-[#0abab5] flex items-center justify-center hover:bg-[#0abab5]/30 active:scale-95 transition"
              >
                <Mic className="w-8 h-8 text-[#0abab5]" />
              </button>
              <span className="text-sm text-white/70">Нажмите, чтобы начать запись</span>

              <div className="w-full flex items-center gap-3 text-[11px] text-white/25">
                <span className="h-px flex-1 bg-white/10" /> или <span className="h-px flex-1 bg-white/10" />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,video/*,.m4a,.mp3,.ogg,.opus,.amr,.3gp,.wav,.wma,.aac,.flac,.webm,.caf,.mp4"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-2.5 rounded-lg text-white/85 text-sm bg-white/[0.04] border-x-[3px] border-y border-white/20 hover:bg-white/10 transition flex items-center justify-center gap-2"
              >
                <Upload className="w-4 h-4" /> Загрузить готовую запись
              </button>
              <p className="text-[11px] text-white/35 text-center">
                Запись с диктофона телефона, созвона или пересланное голосовое
              </p>
            </>
          )}

          {recording && (
            <>
              <div className="flex items-center gap-2 text-red-400 text-sm font-medium">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" /> Идёт запись
              </div>
              <div className="text-3xl font-semibold text-white tabular-nums">
                {pad(Math.floor(durationSec / 60))}:{pad(durationSec % 60)}
              </div>
              <div className="flex items-end gap-[3px] h-12">
                {waveform.map((v, i) => (
                  <span
                    key={i}
                    className="w-1 rounded-full bg-[#0abab5]"
                    style={{ height: `${Math.max(8, v * 100)}%`, opacity: 0.5 + v * 0.5 }}
                  />
                ))}
              </div>
              <button
                onClick={handleStop}
                className="w-full py-2.5 rounded-lg text-white font-medium bg-red-500/20 border-x-[3px] border-y border-red-500 hover:bg-red-500/30 transition flex items-center justify-center gap-2"
              >
                <Square className="w-4 h-4" /> Остановить и отправить
              </button>
              <p className="text-[11px] text-white/40 text-center">
                Участники уведомлены о записи. Голосовые отпечатки не сохраняются.
              </p>
            </>
          )}

          {uploading && (
            <div className="flex flex-col items-center gap-3 py-4 text-white/80 w-full">
              <Loader2 className="w-7 h-7 text-[#0abab5] animate-spin" />
              <div className="text-sm">
                {uploadPercent !== null && uploadPercent < 100
                  ? `Отправка записи — ${uploadPercent}%`
                  : 'Отправка записи…'}
              </div>
              {uploadPercent !== null && (
                <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-[#0abab5] transition-all" style={{ width: `${uploadPercent}%` }} />
                </div>
              )}
            </div>
          )}

          {done && (
            <div className="flex flex-col items-center gap-3 py-2">
              <CheckCircle2 className="w-10 h-10 text-emerald-400" />
              <div className="text-sm text-white text-center font-medium">
                Запись разговора отправлена
              </div>
              <p className="text-[12px] text-white/50 text-center">
                Секретарь обработает её и подготовит протокол.
              </p>
              <a
                href={secretaryUrl}
                target="_blank"
                rel="noreferrer"
                className="text-[12px] text-[#0abab5] hover:underline"
              >
                Открыть Секретарь →
              </a>
              <button onClick={handleClose} className="mt-1 text-xs text-white/50 hover:text-white">
                Закрыть
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SecretaryRecorderSheet;
